import React, { useCallback, useMemo } from 'react';
import { connect, } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormGroup from '@material-ui/core/FormGroup';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { components, utils, } from 'styles';
import SummaryOrderAcceptanceDetailsList from './OrderAcceptanceDetailsList';
import SummaryOrderAcceptanceDetailsListItem from './OrderAcceptanceDetailsListItem';
import { StoreBasicDataApiStructure, } from 'common/types';
import { CheckoutTermsType, } from 'api/repositories/CheckoutTerms';
import SummaryOrderTermDialog, { ChildrenRenderProps, } from './OrderTermDialog';
import { repositories } from 'api';
import { formatDateToPolish } from 'common/functions';

// icons for items
import iconInfo from 'assets/icons/icon_info.svg';
import iconCardCar from 'assets/icons/icon_card_car.svg';

type TermsAcceptanceFieldState = {
    termsAcceptance: boolean,
    isTouched: boolean,
};

type Props = {
    setAcceptance: Function,
    termsAcceptanceFieldState: TermsAcceptanceFieldState,
    checkoutTerms: StoreBasicDataApiStructure<Array<CheckoutTermsType>>,
    shippingForm: string,
    paymentName: string,
    realizationDate: string,
};

type InfoList = {
    icon: string,
    text: string | Array<string>,
};

const renderNestedInfoList = (item: string) => (
    <li key={item}>{item}</li>
);

const renderInfoList = (item: InfoList, index: number) => {
    const preparedItem = Array.isArray(item.text)
        ? <ul>{item.text.map(renderNestedInfoList)}</ul>
        : item.text;

    return (
        <SummaryOrderAcceptanceDetailsListItem key={item.text.toString() + index}
                                               imgPath={item.icon}>
            {preparedItem}
        </SummaryOrderAcceptanceDetailsListItem>
    );
};

const buttonLinkStyles = {
    textAlign: 'left',
    paddingLeft: 40,
};

const SummaryOrderAcceptance: React.FC<Props> = ({
    setAcceptance,
    termsAcceptanceFieldState,
    checkoutTerms,
    shippingForm,
    paymentName,
    realizationDate,
}) => {
    const buttonLinkClasses = utils.makeGenericStyles(buttonLinkStyles);
    const checkboxClasses = components.makeCheckboxRequiredStyles();
    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setAcceptance((prevState: TermsAcceptanceFieldState) => {
            const checked = event.target.checked;

            return !prevState.isTouched
                ? { isTouched: true, termsAcceptance: checked, }
                : { ...prevState, termsAcceptance: checked, };
        });
    }, [ setAcceptance, ]);

    const renderTerms = useCallback(
        ({ content, title, }: CheckoutTermsType) => {
            const renderContent = () => (<div dangerouslySetInnerHTML={{ __html: content, }} />);

            const renderLink = ({ handleClickOpen }: ChildrenRenderProps) => (
                <Link component="button"
                      classes={buttonLinkClasses}
                      variant="body2"
                      onClick={handleClickOpen}>
                    {title}
                </Link>
            );

            return (
                <SummaryOrderTermDialog key={title}
                                        renderContent={renderContent}>
                    {renderLink}
                </SummaryOrderTermDialog>
            );
        },
        [ buttonLinkClasses, ]
    );

    const infoList = useMemo(
        () => [
            {
                icon: iconInfo,
                text: `Termin realizacji zamówienia: ${realizationDate}`,
            },
            {
                icon: iconCardCar,
                text: `Przesyłka kurierska - ${shippingForm.toUpperCase()}`,
            },
            {
                icon: iconInfo,
                text: paymentName,
            },
            {
                icon: iconInfo,
                text: [
                    'Sprawdź czy wszystko się zgadza i kliknij "Zamawiam i płacę".',
                    'Kody dostępu do aplikacji zostaną przesłane na adres e-mail.'
                ],
            },
        ],
        [ paymentName, realizationDate, shippingForm, ]
    );

    const { isTouched, termsAcceptance, } = termsAcceptanceFieldState;

    return (
        <Grid container>
            <Grid item md={6}>
                <SummaryOrderAcceptanceDetailsList>
                    {infoList.map(renderInfoList)}
                </SummaryOrderAcceptanceDetailsList>
            </Grid>
            <Grid item
                  md={6}
                  container
                  direction="column">
                <FormControl required
                             error={!termsAcceptance}
                             component="fieldset">
                    <FormGroup>
                        <FormControlLabel label="Akceptuję regulaminy"
                                          control={<Checkbox checked={termsAcceptance}
                                                             classes={checkboxClasses}
                                                             onChange={handleChange}
                                                             name="termsAcceptance" />}
                        />
                    </FormGroup>
                    {isTouched && !termsAcceptance && <FormHelperText error={!termsAcceptance}>To pole jest obowiązkowe</FormHelperText>}
                </FormControl>
                {checkoutTerms?.data?.map(renderTerms)}
            </Grid>
        </Grid>
    );
};

type MappingType = {
    checkoutTerms: StoreBasicDataApiStructure<Array<CheckoutTermsType>>,
    checkout: {
        data: StoreBasicDataApiStructure<repositories.CheckoutType>,
        values: any,
    },
};

const mapStateToProps = ({ checkoutTerms, checkout, }: MappingType) => {
    const paymentName = Object
        .values(checkout.data.data.payments[0]?.methods || {})
        .find(paymentMethod => paymentMethod.code === checkout.values.paymentMethod)?.name ?? '';
    const realizationDate = checkout.data.data.realizationDates.find(realizationDateItem => realizationDateItem.code === checkout.values.realizationDate);

    return {
        checkoutTerms,
        shippingForm: checkout.values.shippingForm,
        paymentName,
        realizationDate: `${formatDateToPolish(realizationDate?.startAt)} - ${formatDateToPolish(realizationDate?.endAt)}`,
    };
};

export default connect(
    mapStateToProps,
)(SummaryOrderAcceptance);
